import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { RESTORE_FILE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { CornerLeftDown } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { useState } from "react"

interface Props {
  file: DictType
  isMenuView?: boolean
}
const RestoreFile = ({ file, isMenuView = false }: Props) => {
  const navigate = useNavigate()
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [open, setOpen] = useState(false)

  const restoreFile = async () => {
    const response = await axios.post(
      SERVER_URL + RESTORE_FILE_ENDPOINT,
      {
        files_id: [file?.id],
        projects_id: file.project_id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const restoreFileMutation = useMutation({
    mutationKey: ["restore-file", file?.id],
    mutationFn: restoreFile,
    onSuccess: (data) => {
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["get-curr-folder-files"] })
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      queryClient.invalidateQueries({ queryKey: ["deleted-files"] })

      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)

      toast({
        title: translate("Success"),
        description: `${file?.name} ${translate("has been restored")}`,
        variant: "success",
      })
      setOpen(false)
    },
    onError: (error) => {
      setOpen(false)
      toast({
        title: translate("Error"),
        description: `${translate("Unable to restore")} ${file?.name}`,
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog open={open}>
      <Tooltip>
        <TooltipTrigger>
          <AlertDialogTrigger onClick={() => setOpen(true)}>
            {isMenuView ? (
              <div className='flex items-center gap-2'>
                <Button variant={"ghost"} className='h-6 w-7 text-xs' size={"icon"}>
                  <CornerLeftDown className='text-primary' size={16} />
                </Button>
                <span className='text-sm'>{translate("Restore file")}</span>
              </div>
            ) : (
              <Button className='h-6 w-6 text-xs' size={"icon"}>
                <CornerLeftDown className='text-primary' size={16} />
              </Button>
            )}
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent>{translate("Restore files")}</TooltipContent>
      </Tooltip>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {file?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will add the file back to the project")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            {translate("Cancel")}
          </AlertDialogCancel>
          <Button
            disabled={restoreFileMutation?.isPending}
            loading={restoreFileMutation?.isPending}
            onClick={() => restoreFileMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreFile
